<template>
  <figure
    v-touch:swipe.right="onPrevious"
    v-touch:swipe.left="onNext"
    class="image-preview"
    tabindex="0"
    data-test-id="image-preview"
    @keyup.arrow-left="onPrevious"
    @keyup.arrow-right="onNext"
    @keyup.escape="$emit('close')"
  >
    <div class="image-preview__media">
      <a
        :href="image.hostPageURL"
        class="image-preview__image-link"
        rel="noopener"
        target="_blank"
        data-track-id="images-preview-image-link"
      >
        <img
          v-lazy="{
            src: previewUrl,
            loading: image.thumbnailURL,
            error: image.thumbnailURL,
          }"
          :width="image.width"
          :height="image.height"
          :alt="image.name"
          :data-test-src="previewUrl"
          data-test-id="image-preview-image"
          class="image-preview__image"
        >
        <EImagePreviewFallback
          :style="{
            width: `${image.width}px`,
            height: `${image.height}px`,
          }"
          class="image-preview__image-fallback"
          data-test-id="image-preview-image-fallback"
        />
      </a>
    </div>
    <figcaption class="image-preview__details">
      <EIconButton
        class="image-preview__close"
        icon="close"
        icon-size="s"
        size="s"
        variant="solid-dark"
        data-test-id="image-preview-close"
        @click="$emit('close')"
      />
      <div class="image-preview__details-content">
        <a
          class="image-preview__main-link"
          :href="image.hostPageURL"
          target="_blank"
          rel="noopener"
          data-track-id="images-preview-title-link"
        >
          <h2 class="image-preview__heading" data-test-id="image-preview-name">
            {{ image.name }}
          </h2>
          <div class="image-preview__source" data-test-id="image-preview-source">
            {{ source }}
          </div>
        </a>
        <div class="image-preview__dimensions">
          {{ dimensions }}
        </div>
        <div class="image-preview__nav-links">
          <EButton
            as="a"
            :href="image.contentURL"
            variant="solid-dark"
            size="s"
            rel="noopener"
            target="blank"
            data-track-id="images-preview-viewfile"
          >
            {{ $t('images.preview.viewfile') }}
          </EButton>
        </div>
      </div>
      <div class="image-preview__navigation">
        <EIconButton
          class="image-preview__previous"
          size="s"
          icon="chevron-left"
          icon-size="s"
          variant="solid-dark"
          data-test-id="image-preview-previous"
          data-track-id="images-preview-previous"
          :disabled="isFirst"
          @click="onPrevious"
        />
        <EIconButton
          class="image-preview__next"
          size="s"
          icon="chevron-right"
          icon-size="s"
          variant="solid-dark"
          data-test-id="image-preview-next"
          data-track-id="images-preview-next"
          :disabled="isLast"
          @click="onNext"
        />
      </div>
    </figcaption>
  </figure>
</template>

<script>
import { mapState } from 'vuex';

import { hasModifierKey } from '~~/common/js/client/keyboard-utils';
import EButton from '~~/common/vue/components/button';
import EIconButton from '~~/common/vue/components/icon-button';
import { PROXY_IMAGE_SIZE } from '~/constants';
import { getImageSource, getImageDimensions, getImageProxyUrl } from '~/utils/image-data';
import EImagePreviewFallback from './image-preview-fallback';

export default {
  name: 'EImagePreview',
  components: {
    EButton,
    EIconButton,
    EImagePreviewFallback,
  },
  props: {
    image: {
      type: Object,
      required: true,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('user', ['deviceType']),
    source() {
      return getImageSource(this.image);
    },
    dimensions() {
      return getImageDimensions(this.image);
    },
    previewUrl() {
      return this.deviceType === 'pc' ?
        this.image.contentURL :
        getImageProxyUrl(this.image, PROXY_IMAGE_SIZE);
    },
  },
  beforeMount() {
    document.body.classList.add('no-scroll--desktop-down');
  },
  mounted() {
    this.$el.focus();
  },
  beforeDestroy() {
    document.body.classList.remove('no-scroll--desktop-down');
  },
  methods: {
    onPrevious(event) {
      if (this.isFirst || hasModifierKey(event)) {
        return;
      }
      // the v-touch handlers don't provide an actual Event object
      if (event.preventDefault) {
        event.preventDefault();
      }
      this.$emit('previous');
    },
    onNext(event) {
      if (this.isLast || hasModifierKey(event)) {
        return;
      }
      if (event.preventDefault) {
        event.preventDefault();
      }
      this.$emit('next');
    },
  },
};
</script>

<style lang="scss" scoped>
// TODO: temporary values, to be determined by design
$image-preview-mobile-max-height: 100%;
$image-preview-mobile-padding-top: 60px;
$image-preview-checkerboard:
  linear-gradient(45deg, #e0e0e0 25%, transparent 25%, transparent 75%, #e0e0e0 75%, #e0e0e0);
$image-preview-prev-next-size: $space-2l;
$image-preview-prev-next-space: $space-s;

.image-preview {
  background-color: $gray-70;
  color: $gray-10;
  margin-left: 0;
  padding-top: $image-preview-mobile-padding-top;
  display: flex;
  flex-direction: column;

  &:focus {
    // tolerating no focus outline here since the element
    // only shows when open, so that already is a focus state of sorts
    outline: none;
  }

  @include desktop {
    align-items: center;
    flex-direction: row;
    padding: $space-m;
  }
}

.image-preview__media,
.image-preview__details {
  flex: 1 1 auto;

  @include desktop {
    flex: 1 0 50%;
  }
}

.image-preview__media {
  align-items: stretch;
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
  max-height: $image-preview-mobile-max-height;
}

.image-preview__image-link {
  height: auto;
  max-height: 100%;
  position: relative;
  overflow: hidden;
  align-items: center;
  display: flex;
  justify-content: center;
}

.image-preview__details {
  // TODO: non-standard border color, check with DS
  border-top: solid $border-width $gray-60;
  padding: $space-l $space-m;
  margin-top: $space-l;
  flex-grow: 0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @include desktop {
    border-top: none;
    padding: 0 (($image-preview-prev-next-size * 2) + $image-preview-prev-next-space) 0 7.5%;
  }
}

.image-preview__heading {
  @include line-clamp(2);

  font-size: $font-1l;
  font-weight: inherit;
  margin: 0 0 $space-2s;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  @include desktop {
    font-size: $font-2l;
    white-space: nowrap;
    word-wrap: break-word;
  }
}

.image-preview__main-link {
  display: block;

  &:hover .image-preview__heading {
    text-decoration: underline;
  }
}

.image-preview__source,
.image-preview__dimensions {
  color: $gray-30;
  font-size: $font-m;
}

.image-preview__source {
  margin-bottom: $space-2s;
}

.image-preview__image,
.image-preview__image-fallback {
  max-height: 100%;
  max-width: 100%;
  flex: 0 1 0%;
  height: auto;
  width: auto;

  // this is a quick way to detect Firefox
  // sadly in FF flex-basis has some quirks not present in any other browser
  // this meant on mobile only, the previewed image is stretching till full width
  @include firefox {
    flex-basis: auto;
  }
}

.image-preview__image-fallback {
  display: none;
  min-width: max-content;
}

.image-preview__image {
  background: $image-preview-checkerboard, $image-preview-checkerboard;
  background-color: $white;
  background-position: 0 0, $space-s $space-s;
  background-size: $space-l $space-l;
  // used to stop the blur effect from bleeding past the edge of the image
  // TODO: replace with `clip-path: border-box` when supported in all browsers
  // TODO: when standardised in DS, create a mixin inc. the filter and the clip-path
  clip-path: polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%);
  display: block;
  filter: blur(50px);

  &[lazy="loaded"] {
    filter: none;
  }

  &[lazy="error"] {
    display: none;

    ~ .image-preview__image-fallback {
      display: flex;
    }
  }
}

.image-preview__close {
  border-radius: $border-radius-full;
  position: absolute;
  top: $space-m;
  right: $space-m;
  z-index: $z-index-l;

  @include desktop {
    top: $space-l;
    right: auto;
  }
}

.image-preview__nav-links {
  display: flex;
  margin-top: $space-s;
  position: absolute;
  right: $space-m;
  bottom: auto;
  transform: translateY(-$space-3l);

  @include desktop {
    position: static;
    transform: none;
  }
}

.image-preview__navigation {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: $space-l;

  @include desktop {
    bottom: auto;
    left: auto;
    margin-top: 0;
    padding: 0;
    position: absolute;
    right: $space-m;
    top: $space-l;
    width: auto;
  }
}

.image-preview__previous,
.image-preview__next {
  border-radius: $border-radius-full;
}

.image-preview__next {
  margin-left: $space-s;
}
</style>
